import { useCallback, useState } from "react";
import { trainingAPI } from "@api/TrainingAPI";
import { useDashboard } from "../Contexts/dashboardHooks";

/**
 * The dashboard PDF, rendered by the server.
 *
 * Every PDF of a sandbox dashboard comes from one renderer: the Export menu here and
 * the scheduled sandbox-report both call the backend, which builds the render bundle
 * from the saved spec and runs the sandbox-pdf-renderer Lambda. The browser no longer
 * captures charts or builds the document itself, so the two outputs are the same file.
 *
 * A pending auto-save is flushed first, so the server renders the state on screen.
 */
export function useExportPdf() {
  const { state, flushSave } = useDashboard();
  const [exporting, setExporting] = useState(false);

  const buildBlob = useCallback(async (): Promise<Blob> => {
    if (!state.dashboardId) throw new Error("No dashboard is loaded");
    await flushSave();
    return trainingAPI.renderDashboardPdf(state.dashboardId);
  }, [state.dashboardId, flushSave]);

  const exportPdf = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await buildBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${state.name || "dashboard"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF export failed:", err);
    } finally {
      setExporting(false);
    }
  }, [exporting, buildBlob, state.name]);

  // Same render as exportPdf, but returns the blob instead of downloading (used by
  // the "Annotate + Export" and "Send Email" flows). Returns null on failure.
  const generateBlob = useCallback(async (): Promise<Blob | null> => {
    if (exporting) return null;
    setExporting(true);
    try {
      return await buildBlob();
    } catch (err) {
      console.error("PDF generation failed:", err);
      return null;
    } finally {
      setExporting(false);
    }
  }, [exporting, buildBlob]);

  return { exportPdf, generateBlob, exporting };
}
